import {
  buildAuditFieldDiffs,
  formatAuditDisplayVal,
} from "@/lib/activityTimeline/auditDiffDisplay";
import { redactTier2Record, type TimelineDisplayScope } from "@/lib/activityTimeline/tier2Redaction";

type Props = {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  scope: TimelineDisplayScope;
};

export function ActivityTimelineDiffPanel({ before, after, scope }: Props) {
  const diffs = buildAuditFieldDiffs(before, after, scope);
  const changed = diffs.filter((d) => d.changed);
  const unchanged = diffs.filter((d) => !d.changed);

  if (changed.length === 0 && unchanged.length === 0) {
    return <div className="text-xs text-gray-500 dark:text-zinc-400">No snapshot fields.</div>;
  }

  return (
    <div className="space-y-2">
      {changed.length > 0 ? (
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="text-gray-500 dark:text-zinc-400">
              <th className="pb-1 pr-2 font-medium">Field</th>
              <th className="pb-1 pr-2 font-medium">Before</th>
              <th className="pb-1 font-medium">After</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-zinc-700">
            {changed.map((row) => (
              <tr key={row.field}>
                <td className="py-1 pr-2 font-mono text-[11px] text-gray-600 dark:text-zinc-300">
                  {row.field}
                </td>
                <td className="py-1 pr-2 font-mono text-[11px] text-gray-700 dark:text-zinc-200">
                  {formatAuditDisplayVal(row.before)}
                </td>
                <td className="py-1 font-mono text-[11px] text-gray-900 dark:text-zinc-100">
                  {formatAuditDisplayVal(row.after)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-xs text-gray-500 dark:text-zinc-400">No field changes in snapshots.</div>
      )}
      {unchanged.length > 0 ? (
        <details className="text-xs text-gray-500 dark:text-zinc-400">
          <summary className="cursor-pointer hover:text-gray-700 dark:hover:text-zinc-200">
            {unchanged.length} unchanged field{unchanged.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-1 space-y-0.5 font-mono text-[11px]">
            {unchanged.map((row) => (
              <li key={row.field}>
                {row.field}: {formatAuditDisplayVal(row.after)}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

type MetadataProps = {
  metadata: Record<string, unknown>;
  scope: TimelineDisplayScope;
};

export function ActivityTimelineMetadataPanel({ metadata, scope }: MetadataProps) {
  const redacted = redactTier2Record(metadata, scope) ?? {};
  if (Object.keys(redacted).length === 0) {
    return <div className="text-xs text-gray-500 dark:text-zinc-400">No metadata.</div>;
  }
  return (
    <pre className="max-h-48 overflow-auto rounded bg-white px-2 py-2 text-[11px] dark:bg-zinc-900">
      {JSON.stringify(redacted, null, 2)}
    </pre>
  );
}
